"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState, type RefObject, type ReactNode } from "react";
import { cn } from "@/lib/utils";

const delayClassMap = {
  0: "delay-0",
  10: "delay-100",
  15: "delay-150",
  20: "delay-200",
  30: "delay-300",
} as const;

function getDelayClass(delay: number) {
  return delayClassMap[Math.round(delay * 100) as keyof typeof delayClassMap] ?? "delay-0";
}

function useVisible<T extends HTMLElement = HTMLDivElement>(threshold = 0.1): [RefObject<T>, boolean] {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return [ref, visible] as const;
}

function Reveal({
  children,
  delay = 0,
  from = "bottom",
}: {
  children: ReactNode;
  delay?: number;
  from?: "bottom" | "left" | "right";
}) {
  const [ref, visible] = useVisible();

  const fromClasses = {
    bottom: "translate-y-8",
    left: "-translate-x-8",
    right: "translate-x-8",
  } as const;

  return (
    <div
      ref={ref}
      className={cn(
        "opacity-0 transition-all duration-[650ms] ease-out",
        fromClasses[from],
        getDelayClass(delay),
        visible && "opacity-100 translate-x-0 translate-y-0"
      )}
    >
      {children}
    </div>
  );
}

type Service = {
  n: string;
  title: string;
  body: string;
  img: string;
  tags: string[];
};

type TechRow = {
  layer: string;
  tech: string;
};

type Industry = {
  name: string;
  desc: string;
};

type Faq = {
  q: string;
  a: string;
};

type StepData = {
  n: string;
  title: string;
  body: string;
};

const HERO_TAGS = [
  "Custom Software",
  "Web & Mobile",
  "ERP / CRM",
  "Cloud",
  "AI/ML",
];

const TRUST = [
  "Scalable & Secure Architecture",
  "Agile Development with Regular Sprints",
  "24/7 Post-Deployment Support",
  "Guaranteed On-Time Delivery",
];

const SERVICES: Service[] = [
  {
    n: "01",
    title: "Custom Software Development",
    body: "Enterprise-grade custom software built precisely around your unique business processes, operational workflows and strategic objectives — not generic templates adapted to fit.",
    img: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=600&q=80",
    tags: ["Business process automation", "Custom workflow engines", "Scalable architecture"],
  },
  {
    n: "02",
    title: "Web Application Development",
    body: "High-performance, responsive web applications built using modern frameworks. Progressive Web Apps, Single Page Applications, cross-browser compatibility, real-time features, API integrations.",
    img: "https://images.unsplash.com/photo-1547658719-da2b51169166?w=600&q=80",
    tags: ["PWA & SPA", "Real-time features", "API integrations"],
  },
  {
    n: "03",
    title: "Mobile Application Development",
    body: "Native and cross-platform mobile apps for iOS and Android. Native performance optimisation, offline functionality, push notifications, App Store and Play Store deployment support.",
    img: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&q=80",
    tags: ["iOS & Android", "Offline functionality", "Cross-platform"],
  },
  {
    n: "04",
    title: "Enterprise Solutions (ERP/CRM)",
    body: "Comprehensive enterprise systems including ERP, CRM, supply chain management and business intelligence platforms. Enterprise system integration, advanced analytics, multi-tenant architecture.",
    img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&q=80",
    tags: ["ERP & CRM", "BI platforms", "Multi-tenant"],
  },
  {
    n: "05",
    title: "Cloud Solutions & Migration",
    body: "Cloud-native development and seamless migration to AWS, Azure, or Google Cloud. Microservices architecture, containerisation with Docker and Kubernetes, auto-scaling and DevOps pipelines.",
    img: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&q=80",
    tags: ["AWS / Azure / GCP", "Docker & Kubernetes", "DevOps"],
  },
  {
    n: "06",
    title: "Software Maintenance & Support",
    body: "Proactive maintenance and continuous monitoring to ensure peak performance, security and system reliability. Scheduled updates, security patches, performance tuning and bug resolution.",
    img: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&q=80",
    tags: ["Security patches", "Performance tuning", "24/7 support"],
  },
];

const TECH_STACK: TechRow[] = [
  { layer: "Frontend", tech: "React, Angular, Vue.js, TypeScript, Next.js" },
  { layer: "Backend", tech: "Node.js, Python, Java, .NET, Express, FastAPI" },
  { layer: "Mobile", tech: "React Native, Flutter, Swift, Kotlin" },
  { layer: "Databases", tech: "MySQL, PostgreSQL, MongoDB, Redis, Elasticsearch" },
  { layer: "Cloud & DevOps", tech: "AWS, Azure, Google Cloud, Docker, Kubernetes" },
  { layer: "AI & ML", tech: "Python, TensorFlow, PyTorch, OpenAI API, LangChain" },
];

const INDUSTRIES: Industry[] = [
  { name: "Healthcare & Life Sciences", desc: "Patient management, telemedicine, diagnostic systems, HIPAA-compliant applications" },
  { name: "Financial Services & Banking", desc: "Core banking modules, payment gateways, trading platforms, compliance systems" },
  { name: "Retail & E-commerce", desc: "Multi-vendor platforms, inventory management, recommendation engines, POS systems" },
  { name: "Education & Ed-Tech", desc: "Learning management systems, student portals, assessment platforms, live class integration" },
  { name: "Manufacturing", desc: "Production planning, quality management, supply chain, IoT integration" },
  { name: "Logistics & Transportation", desc: "Fleet management, route optimisation, delivery tracking, warehouse management" },
];

const STEPS: StepData[] = [
  { n: "01", title: "Requirements Analysis & Discovery", body: "In-depth evaluation of business objectives, user stories, technical specifications and constraints to define the project scope." },
  { n: "02", title: "Architecture & Planning", body: "Technical architecture design, technology stack selection, database design and project roadmapping with clear milestones." },
  { n: "03", title: "Agile Development", body: "Iterative 2-week sprints with continuous integration, code reviews and daily standups — full visibility throughout." },
  { n: "04", title: "Quality Assurance & Testing", body: "Unit testing, integration testing, UAT, performance testing and security testing before any production deployment." },
  { n: "05", title: "Deployment & Go-Live", body: "Secure production deployment, data migration, system configuration and dedicated go-live support to ensure a smooth launch." },
  { n: "06", title: "Ongoing Maintenance", body: "Continuous monitoring, proactive updates, feature enhancements and 24/7 technical support post-deployment." },
];

const FAQS: Faq[] = [
  { q: "How long does a software project take?", a: "Simple applications take 4–8 weeks. Complex enterprise solutions typically take 3–6 months. We provide a detailed timeline with milestones after the requirements analysis phase." },
  { q: "Do you sign NDAs?", a: "Yes. We sign a mutual NDA before sharing any technical or business information. Confidentiality is a standard part of our engagement process." },
  { q: "What is your pricing model?", a: "We offer fixed-price projects, time-and-material billing and dedicated team models depending on the scope and stage of your project." },
  { q: "Do you provide post-launch support?", a: "Yes. We offer flexible maintenance packages including bug fixes, security updates, performance optimisation and ongoing feature development." },
  { q: "Can I scale the team up mid-project?", a: "Yes. Our agile model allows team scaling based on project requirements with minimal onboarding time — up or down as needed." },
  { q: "Can you integrate with our existing systems?", a: "Yes, we specialise in integrating with legacy systems, ERP, CRM and third-party APIs. We assess compatibility and design the integration architecture before development begins." },
];

function Divider() {
  return <div className="w-11 h-[3px] bg-[#1565C0] mb-6" />;
}

function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn("text-[11px] uppercase tracking-[0.15em] font-semibold text-[#1565C0] mb-3.5 block", className)}>{children}</span>;
}

function CheckIcon() {
  return (
    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#1565C0] text-white flex-shrink-0">
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function TechStackRow({ row, index }: { row: TechRow; index: number }) {
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300", "delay-300"] as const;

  return (
    <div
      ref={ref}
      className={cn(
        "grid grid-cols-[1fr_1.8fr] gap-4 px-4 py-4 border-b border-[#E2E8F0] items-center transition-all duration-500 ease-out",
        index % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]",
        delayClasses[index % delayClasses.length],
        visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-3"
      )}
    >
      <span className="text-[13px] font-semibold text-[#0F172A]">{row.layer}</span>
      <span className="text-[13px] leading-[1.5] text-[#64748B]">{row.tech}</span>
    </div>
  );
}

function IndustryCard({ industry, index }: { industry: Industry; index: number }) {
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300", "delay-300"] as const;

  return (
    <div
      ref={ref}
      className={cn(
        "bg-[#F8FAFC] border-t-4 border-[#1565C0] p-7 transition-all duration-500 ease-out",
        delayClasses[index % delayClasses.length],
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
      )}
    >
      <h3 className="text-[14px] font-semibold text-[#0F172A] mb-2.5">{industry.name}</h3>
      <p className="text-[13.5px] leading-[1.85] text-[#64748B]">{industry.desc}</p>
    </div>
  );
}

function FaqItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false);
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300", "delay-300"] as const;

  return (
    <div
      ref={ref}
      className={cn(
        "border-b border-[#E2E8F0] transition-all duration-500 ease-out",
        delayClasses[index % delayClasses.length],
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
      )}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full bg-transparent border-none py-5 flex items-center justify-between gap-4 text-left cursor-pointer"
      >
        <span className="text-[15px] font-semibold text-[#0F172A] leading-[1.5]">{q}</span>
        <span
          className={cn(
            "flex h-7 w-7 items-center justify-center rounded-full transition-colors duration-200",
            open ? "bg-[#1565C0]" : "bg-[#F8FAFC]"
          )}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d={open ? "M2 6h8" : "M6 2v8M2 6h8"}
              stroke={open ? "#FFFFFF" : "#1565C0"}
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </span>
      </button>

      {open && <p className="pb-5 text-[14px] leading-[1.85] text-[#64748B]">{a}</p>}
    </div>
  );
}

function ServiceCard({ service, index }: { service: Service; index: number }) {
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"] as const;

  return (
    <article
      ref={ref}
      className={cn(
        "group flex flex-col overflow-hidden rounded-sm border border-[#E2E8F0] bg-white shadow-[0_1px_4px_rgba(0,0,0,0.04)] transition-all duration-500 ease-out hover:border-[#CBD5E1] hover:shadow-[0_12px_36px_rgba(0,0,0,0.08)]",
        delayClasses[index % delayClasses.length],
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      <div className="relative h-[170px] overflow-hidden">
        <Image
          src={service.img}
          alt={service.title}
          fill
          className="object-cover transition-transform duration-500 ease-out group-hover:scale-105"
        />
        <span className="absolute top-4 left-4 rounded-sm bg-[#1565C0] px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-white">
          {service.n}
        </span>
      </div>
      <div className="flex flex-col p-[28px] pb-[32px]">
        <h3 className="text-[18px] font-bold text-[#0F172A] mb-2 leading-[1.25]">{service.title}</h3>
        <p className="text-[13.5px] text-[#64748B] mb-5 leading-[1.78]">{service.body}</p>
        <div className="mt-auto flex flex-wrap gap-2">
          {service.tags.map((tag) => (
            <span key={tag} className="rounded-full bg-[#EFF6FF] px-2.5 py-1 text-[11px] font-semibold text-[#1565C0]">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}

function Step({ step, index, last }: { step: StepData; index: number; last: boolean }) {
  const [ref, visible] = useVisible();
  const delayClasses = ["delay-0", "delay-75", "delay-150", "delay-200", "delay-300"] as const;

  return (
    <div
      ref={ref}
      className={cn(
        "relative flex gap-4 items-start pb-7 transition-all duration-[550ms] ease-out",
        delayClasses[index % delayClasses.length],
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
      )}
    >
      {!last && <span className="absolute left-[17px] top-[36px] bottom-0 w-px bg-[#E2E8F0]" />}
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1565C0] text-[14px] font-bold text-white">
        {step.n}
      </div>
      <div className="pt-1">
        <h4 className="text-[14px] font-semibold text-[#0F172A] mb-2">{step.title}</h4>
        <p className="text-[13.5px] leading-[1.85] text-[#64748B]">{step.body}</p>
      </div>
    </div>
  );
}

export default function SoftwarePage() {
  return (
    <main className="bg-white">
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-0 lg:min-h-[450px]">
        <div className="flex flex-col justify-center bg-white py-[30px] pl-4 sm:pl-8 lg:pl-[80px] pr-[30px]">
          <div className="flex flex-wrap items-center gap-2 text-[12px] font-medium text-[#64748B] mb-5">
            <Link href="/services" className="text-[#1565C0] hover:text-[#0D47A1] transition-colors duration-200">
              Services
            </Link>
            <span className="text-[#E2E8F0]">›</span>
            <Link href="/services/it" className="text-[#1565C0] hover:text-[#0D47A1] transition-colors duration-200">
              IT Solutions
            </Link>
            <span className="text-[#E2E8F0]">›</span>
            <span className="text-[#0F172A]">Software Development</span>
          </div>

          <div className="flex flex-wrap gap-2 mb-5">
            {HERO_TAGS.map((tag) => (
              <span key={tag} className="rounded-full bg-[#EFF6FF] px-3 py-1 text-[11px] font-semibold text-[#1565C0]">
                {tag}
              </span>
            ))}
          </div>

          <span className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#1565C0] mb-3.5 block">
            IT Solutions — Software Development
          </span>
          <Divider />
          <h1 className="text-[clamp(26px,3.5vw,46px)] font-extrabold text-[#0F172A] leading-[1.1] mb-6 ">
            Enterprise Software That Powers Business Growth
          </h1>
          <p className="text-[15px] leading-[1.85] text-[#64748B] mb-10 ">
            We design and build secure, scalable, high-performance software solutions — from SaaS platforms to enterprise applications — delivered on time, on budget and built to last.
          </p>

          <div className="flex flex-col gap-3 mb-9">
            {TRUST.map((text, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckIcon />
                <p className="text-[13.5px] leading-[1.78] text-[#334155]">{text}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-4">
            <button className="rounded-sm bg-[#1565C0] px-7 py-3 text-[13px] font-bold uppercase tracking-[0.06em] text-white transition-colors duration-200 hover:bg-[#12579f]">
              START YOUR PROJECT
            </button>
            <button className="rounded-sm border border-[#E2E8F0] bg-white px-7 py-3 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#0F172A] transition-colors duration-200 hover:bg-[#F8FAFC]">
              VIEW CASE STUDIES
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden w-full min-h-[250px] sm:min-h-[340px] lg:min-h-[420px]">
          <Image
            src="/images/services/IT/Banner/Software.png"
            alt="Software development"
            fill
            className="object-cover"
          />
        </div>
      </section>

      <div className="bg-[#1565C0] grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        {[
          { v: "6", l: "Service Areas" },
          { v: "4–8 Wks", l: "Fastest Delivery" },
          { v: "24/7", l: "Post-Launch Support" },
          { v: "NDA", l: "Signed by Default" },
        ].map((item, index) => (
          <div key={index} className={cn("py-5 text-center", index < 3 ? "xl:border-r border-white/15" : "")}> 
            <div className="text-[22px] font-black text-white leading-none">{item.v}</div>
            <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-white/70 mt-1">{item.l}</div>
          </div>
        ))}
      </div>

      <section className="py-8 sm:py-12 lg:py-[60px] px-4 sm:px-8 lg:px-[80px] bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-[56px]">
              <Eyebrow>Software Services</Eyebrow>
              <Divider />
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-[#0F172A] leading-[1.18] max-w-[640px]">
                  Six Development Capabilities. <br />One Integrated Team.
                </h2>
                <p className="text-[14px] leading-[1.75] text-[#64748B] max-w-[360px]">
                  From concept to deployment and beyond — we cover every stage of the software lifecycle.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid gap-6 xl:grid-cols-3 md:grid-cols-2">
            {SERVICES.map((service, index) => (
              <ServiceCard key={service.n} service={service} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-8 sm:py-12 lg:py-[60px] px-4 sm:px-8 lg:px-[80px] bg-white">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-[1fr_1fr] lg:items-center">
          <Reveal from="left">
            <div>
              <Eyebrow>Technology Stack</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(24px,2.8vw,36px)] font-bold text-[#0F172A] leading-[1.18] mb-5">
                Modern. Proven. Production-Ready.
              </h2>
              <p className="text-[15px] leading-[1.85] text-[#64748B] mb-8">
                We work with the best tools for each problem — selecting the right technology for your performance, scale and maintainability requirements.
              </p>
              <div className="grid grid-cols-[1fr_1.8fr] bg-[#1565C0] px-4 py-3 text-white">
                <span className="text-[11px] font-bold uppercase tracking-[0.1em]">Layer</span>
                <span className="text-[11px] font-bold uppercase tracking-[0.1em]">Technologies</span>
              </div>
              <div className="mt-1 overflow-hidden rounded-b-xl">
                {TECH_STACK.map((row, index) => (
                  <TechStackRow key={row.layer} row={row} index={index} />
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <div className="relative">
              <div className="relative w-full h-[480px] overflow-hidden rounded-sm">
                <Image
                  src="https://images.unsplash.com/photo-1518770660439-4636190af475?w=700&q=80"
                  alt="Technology stack"
                  fill
                  className="object-cover"
                />
              </div>
              <div className="hidden lg:block absolute -bottom-6 -right-6 rounded-sm border border-[#E2E8F0] bg-white p-[22px] shadow-[0_8px_32px_rgba(0,0,0,0.10)] min-w-[200px]">
                <div className="text-[30px] font-black text-[#1565C0] leading-none">4–8</div>
                <div className="mt-2 text-[11px] font-medium uppercase tracking-[0.06em] text-[#64748B]">Weeks to Launch</div>
                <div className="mt-4 text-[28px] font-black text-[#E65100] leading-none">2-Week</div>
                <div className="mt-2 text-[11px] font-medium uppercase tracking-[0.06em] text-[#64748B]">Sprint Cycles</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-8 sm:py-12 lg:py-[60px] px-4 sm:px-8 lg:px-[80px] bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-[1fr_1fr] lg:items-start">
          <Reveal from="left">
            <div>
              <Eyebrow>Development Process</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-[#0F172A] leading-[1.18] mb-5">6-Phase Development Process</h2>
              <p className="text-[15px] leading-[1.85] text-[#64748B] mb-10">
                A structured, transparent process with full visibility at every stage — from first requirements workshop to post-launch maintenance.
              </p>
              <div className="relative w-full h-[260px] overflow-hidden rounded-sm">
                <Image
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=700&q=80"
                  alt="Agile development"
                  fill
                  className="object-cover"
                />
              </div>
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="space-y-0">
              {STEPS.map((step, index) => (
                <Step key={step.n} step={step} index={index} last={index === STEPS.length - 1} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-8 sm:py-12 lg:py-[60px] px-4 sm:px-8 lg:px-[80px] bg-white">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-[56px]">
              <Eyebrow>Industries We Serve</Eyebrow>
              <Divider />
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-[#0F172A] leading-[1.18] max-w-[640px]">
                  Domain Expertise Across <br />Six Key Sectors
                </h2>
                <p className="text-[14px] leading-[1.75] text-[#64748B] max-w-[360px]">
                  Deep understanding of industry-specific workflows, compliance and technology requirements.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            {INDUSTRIES.map((industry, index) => (
              <IndustryCard key={industry.name} industry={industry} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-8 sm:py-12 lg:py-[60px] px-4 sm:px-8 lg:px-[80px] bg-[#F8FAFC]">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-[1fr_1fr] lg:items-start">
          <Reveal from="left">
            <div>
              <Eyebrow>Frequently Asked Questions</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-[#0F172A] leading-[1.18] mb-6">Common Questions About Software Development</h2>
              <p className="text-[15px] leading-[1.85] text-[#64748B] mb-10">
                Clear answers to the questions we hear most often from businesses evaluating a custom software project.
              </p>
              <div className="relative w-full h-[180px] sm:h-[240px] lg:h-[280px] overflow-hidden rounded-sm">
                <Image
                  src="https://images.unsplash.com/photo-1560472355-536de3962603?w=700&q=80"
                  alt="Software FAQ"
                  fill
                  className="object-cover"
                />
              </div>
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="border-t border-[#E2E8F0]">
              {FAQS.map((faq, index) => (
                <FaqItem key={faq.q} q={faq.q} a={faq.a} index={index} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-8 sm:py-12 lg:py-[60px] px-4 sm:px-8 lg:px-[80px] bg-white">
        <div className="max-w-[1160px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-[1fr_1fr] lg:items-center">
          <Reveal from="left">
            <div className="relative w-full h-[220px] sm:h-[300px] lg:h-[380px] overflow-hidden rounded-sm">
              <Image
                src="https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=800&q=80"
                alt="Start your project"
                fill
                className="object-cover"
              />
            </div>
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div>
              <Eyebrow>Get Started</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(26px,3vw,40px)] font-bold text-[#0F172A] leading-[1.15] mb-4">
                Choose Your Path <br />to Success
              </h2>
              <p className="text-[15px] leading-[1.85] text-[#64748B] mb-9">
                Each service is tailored to your specific business needs. Book a free discovery call and let our experts identify the right solution for you - at no obligation.
              </p>
              <div className="flex flex-wrap gap-4">
                <button className="rounded-sm bg-[#1565C0] px-8 py-3 text-[13px] font-bold uppercase tracking-[0.06em] text-white transition-colors duration-200 hover:bg-[#12579f]">
                  BOOK A FREE CALL
                </button>
                <button className="rounded-sm border border-[#E2E8F0] bg-white px-8 py-3 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#0F172A] transition-colors duration-200 hover:bg-[#F8FAFC]">
                  CONTACT US
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
